import type { ItemUnit } from '../types';

/**
 * Categories for grouping related units that can be converted between.
 */
export type UnitCategory =
  | 'discrete'
  | 'volume_metric'
  | 'volume_imperial'
  | 'weight_metric'
  | 'weight_imperial';

/**
 * Get the category for a given unit.
 */
export function getUnitCategory(unit: ItemUnit): UnitCategory {
  switch (unit) {
    case 'item':
      return 'discrete';
    case 'ml':
    case 'l':
      return 'volume_metric';
    case 'floz':
    case 'cup':
    case 'pt':
    case 'qt':
    case 'gal':
      return 'volume_imperial';
    case 'g':
    case 'kg':
      return 'weight_metric';
    case 'oz':
    case 'lb':
      return 'weight_imperial';
    default:
      return 'discrete';
  }
}

/**
 * Conversion factors to base units (ml for volume, g for weight).
 * For imperial volume, we use ml as the common base.
 * For imperial weight, we use g as the common base.
 */
const UNIT_TO_BASE: Record<ItemUnit, number> = {
  // Discrete (no conversion)
  item: 1,
  // Volume - metric (base: ml)
  ml: 1,
  l: 1000,
  // Volume - imperial (converted to ml for cross-system)
  floz: 29.5735, // 1 fl oz = 29.5735 ml
  cup: 236.588, // 1 cup = 236.588 ml (8 fl oz)
  pt: 473.176, // 1 pint = 473.176 ml (2 cups)
  qt: 946.353, // 1 quart = 946.353 ml (2 pints)
  gal: 3785.41, // 1 gallon = 3785.41 ml (4 quarts)
  // Weight - metric (base: g)
  g: 1,
  kg: 1000,
  // Weight - imperial (converted to g for cross-system)
  oz: 28.3495, // 1 oz = 28.3495 g
  lb: 453.592, // 1 lb = 453.592 g (16 oz)
};

/**
 * Get all units that can be converted to/from the given unit.
 * Returns all volume units for volume types, all weight units for weight types.
 * Returns only 'item' for discrete type (no conversion possible).
 */
export function getRelatedUnits(unit: ItemUnit): ItemUnit[] {
  const category = getUnitCategory(unit);

  switch (category) {
    case 'discrete':
      return ['item'];
    case 'volume_metric':
    case 'volume_imperial':
      // Return all volume units (metric + imperial) for cross-system conversion
      return ['ml', 'l', 'floz', 'cup', 'pt', 'qt', 'gal'];
    case 'weight_metric':
    case 'weight_imperial':
      // Return all weight units (metric + imperial) for cross-system conversion
      return ['g', 'kg', 'oz', 'lb'];
    default:
      return [unit];
  }
}

/**
 * Check if two units can be converted between each other.
 * Volume units (metric and imperial) are convertible.
 * Weight units (metric and imperial) are convertible.
 * Discrete items cannot be converted.
 */
export function canConvertUnits(fromUnit: ItemUnit, toUnit: ItemUnit): boolean {
  if (fromUnit === toUnit) return true;

  const fromCategory = getUnitCategory(fromUnit);
  const toCategory = getUnitCategory(toUnit);

  // Discrete units cannot be converted
  if (fromCategory === 'discrete' || toCategory === 'discrete') {
    return false;
  }

  // Volume units (both metric and imperial) can convert between each other
  const isFromVolume = fromCategory === 'volume_metric' || fromCategory === 'volume_imperial';
  const isToVolume = toCategory === 'volume_metric' || toCategory === 'volume_imperial';
  if (isFromVolume && isToVolume) return true;

  // Weight units (both metric and imperial) can convert between each other
  const isFromWeight = fromCategory === 'weight_metric' || fromCategory === 'weight_imperial';
  const isToWeight = toCategory === 'weight_metric' || toCategory === 'weight_imperial';
  if (isFromWeight && isToWeight) return true;

  return false;
}

/**
 * Convert a quantity from one unit to another.
 * Returns null if conversion is not possible.
 * Rounds to 3 decimal places to avoid floating-point precision issues.
 *
 * @param quantity - The quantity to convert
 * @param fromUnit - The source unit
 * @param toUnit - The target unit
 * @returns The converted quantity, or null if conversion not possible
 *
 * @example
 * convertQuantity(1000, 'ml', 'l')   // 1
 * convertQuantity(1, 'cup', 'ml')    // 236.588
 * convertQuantity(1, 'lb', 'g')      // 453.592
 * convertQuantity(500, 'ml', 'floz') // ~16.907
 */
export function convertQuantity(
  quantity: number,
  fromUnit: ItemUnit,
  toUnit: ItemUnit
): number | null {
  if (fromUnit === toUnit) return quantity;

  if (!canConvertUnits(fromUnit, toUnit)) {
    return null;
  }

  // Convert: fromUnit -> base unit -> toUnit
  const toBase = quantity * UNIT_TO_BASE[fromUnit];
  const result = toBase / UNIT_TO_BASE[toUnit];

  // Round to 3 decimal places
  return Math.round(result * 1000) / 1000;
}

// ============================================================
// Conversion Reference Data
// ============================================================

/**
 * A single conversion reference entry.
 */
export interface UnitConversionReference {
  /** Human-readable conversion (e.g., "1 cup = 237 mL") */
  display: string;
}

/**
 * Get common conversion references for a unit.
 * Used by both web and mobile to display quick reference info.
 *
 * @param unit - The unit to get references for
 * @returns Array of conversion references
 */
export function getConversionReferences(unit: ItemUnit): UnitConversionReference[] {
  switch (unit) {
    // Metric volume
    case 'ml':
      return [
        { display: '1 cup = 237 mL' },
        { display: '1 fl oz = 30 mL' },
        { display: '1000 mL = 1 L' },
      ];
    case 'l':
      return [
        { display: '1 L = 1000 mL' },
        { display: '1 L \u2248 4.2 cups' },
        { display: '1 gal \u2248 3.8 L' },
      ];
    // Imperial volume
    case 'floz':
      return [
        { display: '1 fl oz \u2248 30 mL' },
        { display: '8 fl oz = 1 cup' },
        { display: '16 fl oz = 1 pt' },
      ];
    case 'cup':
      return [
        { display: '1 cup = 237 mL' },
        { display: '1 cup = 8 fl oz' },
        { display: '2 cups = 1 pt' },
        { display: '4 cups = 1 qt' },
      ];
    case 'pt':
      return [
        { display: '1 pt = 2 cups' },
        { display: '1 pt = 16 fl oz' },
        { display: '1 pt \u2248 473 mL' },
        { display: '2 pt = 1 qt' },
      ];
    case 'qt':
      return [
        { display: '1 qt = 2 pt' },
        { display: '1 qt = 4 cups' },
        { display: '1 qt \u2248 946 mL' },
        { display: '4 qt = 1 gal' },
      ];
    case 'gal':
      return [
        { display: '1 gal = 4 qt' },
        { display: '1 gal = 16 cups' },
        { display: '1 gal \u2248 3.8 L' },
      ];
    // Metric weight
    case 'g':
      return [
        { display: '1000 g = 1 kg' },
        { display: '1 oz \u2248 28 g' },
        { display: '100 g \u2248 3.5 oz' },
      ];
    case 'kg':
      return [
        { display: '1 kg = 1000 g' },
        { display: '1 kg \u2248 2.2 lb' },
        { display: '1 lb \u2248 0.45 kg' },
      ];
    // Imperial weight
    case 'oz':
      return [
        { display: '1 oz \u2248 28 g' },
        { display: '16 oz = 1 lb' },
        { display: '8 oz = \u00BD lb' },
      ];
    case 'lb':
      return [
        { display: '1 lb = 16 oz' },
        { display: '1 lb \u2248 454 g' },
        { display: '1 lb \u2248 0.45 kg' },
      ];
    // Discrete items
    case 'item':
    default:
      return [];
  }
}
